import { FC, useCallback } from "react";
import { IconButton, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import type { GridApi } from "ag-grid-community";
import { useAppSelector } from "@/store/hooks";
import { liveUsageMgmtResourceTypeCsvDataFileNameSelector } from "@/store/live-usage-mgmt/selectors/resource-type-data/liveUsageMgmtResourceTypeCsvDataFileNameSelector";
import { ResourceType } from "@/services/cloudchipr.api";
import {
  resourceIdentifierColDefCSVValueGetter,
  resourceIdentifierColumnDefColId,
} from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceIdentifierColumnDef";
import { AllTypeResourcesData } from "@/store/resources-grid/utils/types/resources";
import { generateAllChildrenCSVData } from "@/components/pages/live-usage-mgmt/utils/helpers/generateAllChildrenCSVData";

interface LiveUsageResourceGridCsvExportProps {
  resourceType: ResourceType;
  gridApi: GridApi | null;
}

export const LiveUsageResourceGridCsvExport: FC<
  LiveUsageResourceGridCsvExportProps
> = ({ resourceType, gridApi }) => {
  const fileName = useAppSelector((state) =>
    liveUsageMgmtResourceTypeCsvDataFileNameSelector(state, resourceType),
  );

  const exportDataAsCsv = useCallback(() => {
    gridApi?.exportDataAsCsv({
      fileName: `${fileName}.csv`,
      processCellCallback: (params) => {
        if (params.column.getColId() === resourceIdentifierColumnDefColId) {
          return resourceIdentifierColDefCSVValueGetter(params);
        }

        return params.value;
      },

      getCustomContentBelowRow: ({ node }) => {
        const childResources = node.data?.child_resource
          ?.resources as AllTypeResourcesData[];

        if (!childResources) {
          return;
        }

        const totalColCount =
          gridApi?.getColumnDefs()?.filter((def) => {
            return (
              "colId" in def &&
              def.colId !== "resourceGridExpanderColumnDefColId" &&
              "hide" in def &&
              !def.hide
            );
          })?.length ?? 0;

        return generateAllChildrenCSVData(childResources, totalColCount);
      },
    });
  }, [gridApi, fileName]);

  return (
    <Tooltip title={`${fileName}_csv`} arrow placement="top">
      <IconButton size="medium" onClick={exportDataAsCsv}>
        <DownloadOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};
